import * as React from 'react';

import FormEditingHandler from '../../../containers/FormEditingHandler';
import DocumentWrapper from '../../../components/DocumentWrapper';
import DocumentHeader from '../../../components/DocumentHeader';
import PlatformConditional from '../../../components/PlatformConditional';
import HistorySection from '../../../components/HistorySection';
import DocumentCreationButton from '../../../components/DocumentCreationButton';

import DiagnosisHistory from './HistoryRowData/DiagnosisHistory';
import MsSymptoms from './HistoryRowData/MSHistory/MsSymptoms';
import ParkinsonSymptoms from './HistoryRowData/ParkinsonHistory/ParkinsonSymptoms';

import Form from './Form';
import { parkinsonCriteriaTypes, TDocument } from '../utils/definitions';
import { fm } from 'Components/FormatMessage';
import { chooseDocumentHeader } from '../utils/functions';
import MSDiagnosticCriteriaHistory from './HistoryRowData/MSHistory/MsDiagnosticsCriteria/MS';
import NMOSDDiagnosticCriteriaHistory from './HistoryRowData/MSHistory/MsDiagnosticsCriteria/NMOSD';
import { assertCapabilities } from '../../../store/index';
import PlatformCapabilities from '../../../config/capabilities';
import { ICapabilityContextProps, withCapabilities } from '../../../containers/CapabilityHandler';
import HuntingtonSymptoms from './HistoryRowData/HuntingtonHistory/HuntingtonSymptoms';

import {
  FormContextProvider,
  HistoryContextProvider,
  IHistoryContext,
  withHistoryContext,
} from '../../../containers/FormContextHandler';
import HTT from './HistoryRowData/HuntingtonHistory/HTT';
import UHDRS from './HistoryRowData/HuntingtonHistory/UHDRS';
import SeizureTypeHistory from './HistoryRowData/EpilepsyHistory/SeizureType';
import SyndromeHistory from './HistoryRowData/EpilepsyHistory/Syndrome';
import EtiologyHistory from './HistoryRowData/EpilepsyHistory/Etiology';
import FirstVisitSleepApnea from './HistoryRowData/SleepApneaHistory/FirstVisitSleepApnea';
import FirstVisitRespiratory from './HistoryRowData/SleepApneaHistory/FirstVisitRespiratory';
import SleepApneaSeverity from './HistoryRowData/SleepApneaHistory/SleepApneaSeverity';
import PerformanceRating from './HistoryRowData/SleepApneaHistory/PerformanceRating';
import NINMTHistory from './HistoryRowData/NINMTHistory';
import { useAppSelector } from 'Store/index';
import HistoryTabs from 'Components/HistoryTabs';
import { generateDxToPlatfMap } from '../utils/Diagnosis';
import MgravisSymptoms from './HistoryRowData/MgravisHistory/MgravisSymptoms';
import MGFA from './HistoryRowData/MgravisHistory/MGFA';
import ParkinsonDiagnosticsCriteria from './HistoryRowData/ParkinsonHistory/ParkinsonDiagnosticsCriteria';

const MsHistoryContentComponent = (props: IHistoryContext & ICapabilityContextProps): React.JSX.Element => {
  const { capabilityGroups, documents, fm, startEdit, view } = props;
  const msDiagnosCriteriaDocs = documents?.filter((d: TDocument) => d?._type === 'msDiagnosticCriteria');
  const nmosdDiagnosCriteriaDocs = documents?.filter((d: TDocument) => d?._type === 'nmosdDiagnosticCriteria');
  const msSymptomDocs = documents?.filter((d: TDocument) => d?._type === 'msSymptoms');
  return (
    <React.Fragment>
      <MsSymptoms documents={msSymptomDocs} fm={fm} startEdit={startEdit} />
      <HistorySection headerText={fm('diagnosis.msDiagnosticCriteria.title')} hasHistoryRows={true}>
        <MSDiagnosticCriteriaHistory fm={fm} startEdit={startEdit} documents={msDiagnosCriteriaDocs} view={view} />
        {assertCapabilities([PlatformCapabilities.NMOSD_CAPABILITES], capabilityGroups ?? {}) ? (
          <NMOSDDiagnosticCriteriaHistory
            documents={nmosdDiagnosCriteriaDocs}
            fm={fm}
            startEdit={startEdit}
            view={view}
          />
        ) : undefined}
      </HistorySection>
    </React.Fragment>
  );
};

const MsHistoryContent = withCapabilities(withHistoryContext(MsHistoryContentComponent));

const ParkinsonHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const parkSymptomsDocs = props?.documents?.filter((doc: TDocument) => doc?._type === 'parkinsonSymptoms') ?? [];
  const parkDiagCriteriaDocs =
    props?.documents?.filter((doc: TDocument) => parkinsonCriteriaTypes.includes(doc?._type)) ?? [];
  return (
    <React.Fragment>
      <ParkinsonSymptoms {...props} documents={parkSymptomsDocs} />
      <ParkinsonDiagnosticsCriteria {...props} documents={parkDiagCriteriaDocs} />
    </React.Fragment>
  );
};

const ParkinsonHistoryContent = withHistoryContext(ParkinsonHistoryContentComponent);

const HuntingtonHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const huntingtonSymptomsDocs =
    props?.documents?.filter((doc: TDocument) => doc?._type === 'huntingtonSymptoms') ?? [];
  const httDocs = props?.documents?.filter((doc: TDocument) => doc?._type === 'htt') ?? [];
  const uhdrsDocs = props?.documents?.filter((doc: TDocument) => doc?._type === 'uhdrsConfidenceIndex') ?? [];
  return (
    <React.Fragment>
      <HuntingtonSymptoms {...props} documents={huntingtonSymptomsDocs} />
      <HTT {...props} documents={httDocs} />
      <UHDRS {...props} documents={uhdrsDocs} />
    </React.Fragment>
  );
};

const HuntingtonHistoryContent = withHistoryContext(HuntingtonHistoryContentComponent);

const EpilepsyHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const etiologyDocs = props?.documents?.filter((doc: TDocument) => doc?._type === 'etiology') ?? [];
  const seizureTypeDocs = props?.documents?.filter((doc: TDocument) => doc?._type === 'seizureType') ?? [];
  const syndromeDocs = props?.documents?.filter((doc: TDocument) => doc?._type === 'syndrome') ?? [];
  return (
    <React.Fragment>
      <SeizureTypeHistory {...props} documents={seizureTypeDocs} />
      <SyndromeHistory {...props} documents={syndromeDocs} />
      <EtiologyHistory {...props} documents={etiologyDocs} />
    </React.Fragment>
  );
};

const EpilepsyHistoryContent = withHistoryContext(EpilepsyHistoryContentComponent);

const MgravisHistoryContentComponent = (props: IHistoryContext): React.JSX.Element => {
  const mgSymptomsDocs = props?.documents?.filter((doc: TDocument) => doc?._type === 'mgravisSymptoms') ?? [];
  const mgfaDocs = props?.documents?.filter((doc: TDocument) => doc?._type === 'mgfa') ?? [];
  return (
    <React.Fragment>
      <MgravisSymptoms {...props} documents={mgSymptomsDocs} />
      <MGFA {...props} documents={mgfaDocs} />
    </React.Fragment>
  );
};

const MgravisHistoryContent = withHistoryContext(MgravisHistoryContentComponent);

const SleepApneaHistoryContent = ({ documents, startEdit, fm }: IHistoryContext<TDocument>): React.JSX.Element => {
  const diagDocs = documents?.filter(d => d._type === 'diagnosis') as IDiagnosis[];
  const sleepApneaDG = diagDocs?.find(d => d.diagnosis === 'G47.3');
  const sleepApneaSusDG = diagDocs?.find(d => d.diagnosis === 'sleepApneaSuspicion');
  const respiratoryDG = diagDocs?.find(d => d.diagnosis === 'J96.1' || d.diagnosis === 'J96.9');
  const respiratorySusDG = diagDocs?.find(d => d.diagnosis === 'respiratoryFailureSuspicion');

  const sleepApneaFirstVisitDocs = documents?.filter(d => d._type === 'sleepApneaFirstVisit');
  const sleepApneaSeverityDocs = documents?.filter(d => d._type === 'sleepApneaSeverity');
  const respFirstVisitDocs = documents?.filter(d => d._type === 'respiratoryFirstVisit');
  const performanceDocs = documents?.filter(d => d._type === 'performanceRating');
  return (
    <React.Fragment>
      {(sleepApneaDG || sleepApneaSusDG) && (
        <React.Fragment>
          <FirstVisitSleepApnea documents={sleepApneaFirstVisitDocs} fm={fm} startEdit={startEdit} />
          {sleepApneaDG && (
            <SleepApneaSeverity
              documents={sleepApneaSeverityDocs as ISleepApneaSeverity[]}
              fm={fm}
              startEdit={startEdit}
            />
          )}
        </React.Fragment>
      )}
      {(respiratoryDG || respiratorySusDG) && (
        <FirstVisitRespiratory documents={respFirstVisitDocs} fm={fm} startEdit={startEdit} />
      )}
      <PerformanceRating documents={performanceDocs as IPerformanceRating[]} fm={fm} startEdit={startEdit} />
    </React.Fragment>
  );
};

const SleepApneaHistoryContentWithContext = withHistoryContext(SleepApneaHistoryContent);

const NinmtHistoryContent = (): React.JSX.Element => <NINMTHistory />;

const platformsWithDiagnosis: Platform[] = [
  'sma',
  'dmd',
  'parkinson',
  'ms',
  'huntington',
  'epilepsy',
  'sleepApnea',
  'mgravis',
];

const DiagnosisHistoryContent = ({ documents }: { documents: TDocument[] }): React.JSX.Element => {
  const { selected, available, preferred } = useAppSelector((s: IState) => s.session.platforms) || {
    selected: '',
    available: [],
  };

  const showOthers = available.length > 1 || (preferred || []).filter(p => p !== selected).length > 0;

  const dgDocs = documents.filter(d => d._type === 'diagnosis') as IDiagnosis[];
  const platformMap = generateDxToPlatfMap();

  const platformDocs = dgDocs.filter(d => d.diagnosis && platformMap[d.diagnosis] === selected);
  const otherDocs = dgDocs.filter(d => d.diagnosis && platformMap[d.diagnosis] !== selected);

  return (
    <React.Fragment>
      <PlatformConditional platform={platformsWithDiagnosis}>
        <HistoryTabs>
          {[
            {
              title: `header.platforms.${selected}`,
              content: (
                <>
                  {platformDocs.length === 0 ? (
                    <div style={{ marginBottom: '4rem' }}>{fm('diagnosis.noDiagnoses')}</div>
                  ) : (
                    <DiagnosisHistory documents={platformDocs} />
                  )}
                  <PlatformConditional platform="ms">
                    <MsHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="parkinson">
                    <ParkinsonHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="huntington">
                    <HuntingtonHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="epilepsy">
                    <EpilepsyHistoryContent />
                  </PlatformConditional>
                  <PlatformConditional platform="sleepApnea">
                    <SleepApneaHistoryContentWithContext />
                  </PlatformConditional>
                  <PlatformConditional platform="mgravis">
                    <MgravisHistoryContent />
                  </PlatformConditional>
                </>
              ),
            },
            ...(showOthers
              ? [
                  {
                    title: 'diagnosis.otherDiagnoses',
                    content: <DiagnosisHistory documents={otherDocs} />,
                    count: otherDocs.length,
                    disabled: otherDocs.length === 0,
                  },
                ]
              : []),
          ]}
        </HistoryTabs>
      </PlatformConditional>

      <PlatformConditional platform="ninmt">
        <NinmtHistoryContent />
      </PlatformConditional>
    </React.Fragment>
  );
};

const DiagnosisDocumentHeader = ({
  name,
  headerId,
  editing,
  editButtons,
}: React.ComponentProps<typeof DocumentHeader>): React.JSX.Element => {
  const props = { name, headerId, editing, editButtons };
  return (
    <React.Fragment>
      <PlatformConditional platform="ninmt">
        <DocumentHeader
          {...props}
          headerId={editing ? props.headerId : 'diagnosis.basicTreatmentInformation'}
          infoText={
            !editing
              ? fm('diagnosis.ninmt.infoText')
              : props.headerId === 'diagnosis.ninmt.contraIndicationsToTreatment.title'
                ? fm('diagnosis.ninmt.contraIndicationsToTreatment.description')
                : props.headerId === 'diagnosis.basicTreatmentInformation'
                  ? fm('diagnosis.ninmt.infoText')
                  : props.headerId === 'diagnosis.ninmt.symptomsAndLocations.title'
                    ? fm('diagnosis.ninmt.symptomsAndLocationsInfoText')
                    : undefined
          }
          editButtons={undefined}
        />
      </PlatformConditional>
      <PlatformConditional platform={platformsWithDiagnosis}>
        <DocumentHeader {...props} infoText={!editing ? fm('diagnosis.infoText') : undefined} />
      </PlatformConditional>
    </React.Fragment>
  );
};

const Diagnosis = ({ documents }: IDiagnosisProps): React.JSX.Element => {
  const [diagnosisSelected, setDiagnosisSelected] = React.useState<boolean>(true);

  const platform = useAppSelector((s: IState) => s.session.platforms?.selected);

  return (
    <FormEditingHandler
      name="diagnosis"
      documents={documents}
      toolbarProps={{
        saveButtonDisabled: !diagnosisSelected,
      }}
    >
      {(editing, startEdit, formData, view, saveDraft): React.JSX.Element => {
        const docID = editing ?? view?.viewing;
        const editingNinmtTreatmentPeriod = !!documents.find(
          d => d._id === editing && d._type === 'ninmtTreatmentPeriod',
        );
        return (
          <DocumentWrapper>
            <DiagnosisDocumentHeader
              name="diagnosis"
              headerId={`diagnosis.${chooseDocumentHeader(documents, editing ?? view?.viewing)}`}
              editing={docID}
              editButtons={
                <DocumentCreationButton
                  name="diagnosis"
                  text="general.new"
                  onClick={(e: React.MouseEvent): void => {
                    // In case a new diagnosis document is created, disable save button until diagnosis is set
                    setDiagnosisSelected(false);
                    startEdit({}, 'diagnosis')(e);
                  }}
                />
              }
            />
            {!docID || (platform === 'ninmt' && editingNinmtTreatmentPeriod) ? (
              <HistoryContextProvider context={{ documents: documents, startEdit: startEdit, view: view }}>
                <FormContextProvider
                  context={{
                    editing: editing,
                    formData: formData,
                    saveDraft: saveDraft,
                  }}
                >
                  <DiagnosisHistoryContent documents={documents} />
                </FormContextProvider>
              </HistoryContextProvider>
            ) : null}
            {docID ? (
              <FormContextProvider
                context={{
                  documents: documents,
                  editing: editing,
                  formData: formData,
                  view: view,
                }}
              >
                <Form setDiagnosisSelected={setDiagnosisSelected} />
              </FormContextProvider>
            ) : null}
          </DocumentWrapper>
        );
      }}
    </FormEditingHandler>
  );
};

interface IDiagnosisProps {
  documents: Array<TDocument>;
}

export default Diagnosis;
